import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useGifStore } from './gif-store';
import { GifFrame, GIF_LIMITS } from './types';
import { Trash2, GripVertical, Images, AlertTriangle, X, Film, Image as ImageIcon, ChevronUp, ChevronDown } from 'lucide-react';

interface SortableFrameProps {
  frame: GifFrame;
  index: number;
  total: number;
  onRemove: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
}

function SortableFrame({ frame, index, total, onRemove, onMoveUp, onMoveDown }: SortableFrameProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: frame.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        relative group rounded-lg border-2 overflow-hidden bg-card
        ${isDragging ? 'border-primary shadow-lg ring-2 ring-primary/20' : 'border-muted'}
      `}
    >
      {/* Frame number badge */}
      <Badge 
        variant="secondary" 
        className="absolute top-1 left-1 z-10 text-xs font-mono"
      >
        {index + 1}
      </Badge>

      {/* Drag handle (desktop) */}
      <button
        {...attributes}
        {...listeners}
        className="hidden sm:block absolute top-1 right-1 z-10 p-1 rounded bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing transition-colors"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Mobile reorder buttons (tap-to-move, F7) */}
      <div className="sm:hidden absolute top-1 right-1 z-10 flex flex-col gap-0.5">
        <button
          onClick={() => onMoveUp(frame.id)}
          disabled={index === 0}
          className="p-1 rounded bg-background/90 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Move frame earlier"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => onMoveDown(frame.id)}
          disabled={index === total - 1}
          className="p-1 rounded bg-background/90 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Move frame later"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {/* Remove button */}
      <button
        onClick={() => onRemove(frame.id)}
        className="absolute bottom-1 right-1 z-10 p-1 rounded bg-destructive/80 hover:bg-destructive text-destructive-foreground sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
        aria-label="Remove frame"
      >
        <X className="h-3 w-3" />
      </button>

      {/* Frame image */}
      <div className="aspect-video bg-muted">
        <img
          src={frame.imageData}
          alt={`Frame ${index + 1}`}
          className="w-full h-full object-contain"
          draggable={false}
        />
      </div>

      {/* Duration indicator */}
      {frame.duration && (
        <div className="absolute bottom-1 left-1 z-10">
          <Badge variant="outline" className="text-xs bg-background/80">
            {frame.duration}ms
          </Badge>
        </div>
      )}
    </div>
  );
}

interface GifFrameSelectorProps {
  className?: string;
}

export function GifFrameSelector({ className }: GifFrameSelectorProps) {
  const navigate = useNavigate();
  const frames = useGifStore((state) => state.frames);
  const reorderFrames = useGifStore((state) => state.reorderFrames);
  const removeFrame = useGifStore((state) => state.removeFrame);
  const clearFrames = useGifStore((state) => state.clearFrames);
  const moveFrameUp = useGifStore((state) => state.moveFrameUp);
  const moveFrameDown = useGifStore((state) => state.moveFrameDown);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedFrames = useMemo(
    () => [...frames].sort((a, b) => a.order - b.order),
    [frames]
  );

  const frameIds = useMemo(
    () => sortedFrames.map((f) => f.id),
    [sortedFrames]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedFrames.findIndex((f) => f.id === active.id);
      const newIndex = sortedFrames.findIndex((f) => f.id === over.id);
      reorderFrames(oldIndex, newIndex);
    }
  };

  const showWarning = frames.length > GIF_LIMITS.WARNING_FRAME_COUNT;

  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Images className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Frames</CardTitle>
            <Badge variant="secondary" className="ml-2">
              {frames.length}
            </Badge>
          </div>
          {frames.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFrames}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
        <CardDescription>
          <span className="hidden sm:inline">Drag frames to reorder.</span>
          <span className="sm:hidden">Tap arrows to reorder.</span>{' '}
          The GIF will play in this sequence.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showWarning && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <p className="text-sm">
              You have {frames.length} frames. GIFs with many frames can be large and slow to encode.
              Consider removing some frames for better performance.
            </p>
          </div>
        )}

        {frames.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Images className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground mb-4">No frames added yet</p>
            <p className="text-sm text-muted-foreground/70 mb-6">
              Select frames from the video player or frame library
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/?view=studio')}
                className="gap-2"
              >
                <Film className="h-4 w-4" />
                From Video
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/?view=library&library=frames')}
                className="gap-2"
              >
                <ImageIcon className="h-4 w-4" />
                From Library
              </Button>
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={frameIds} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {sortedFrames.map((frame, index) => (
                  <SortableFrame
                    key={frame.id}
                    frame={frame}
                    index={index}
                    total={sortedFrames.length}
                    onRemove={removeFrame}
                    onMoveUp={moveFrameUp}
                    onMoveDown={moveFrameDown}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {frames.length > 0 && (
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {frames.length} frame{frames.length !== 1 ? 's' : ''} selected
            </span>
            <span>
              Max: {GIF_LIMITS.MAX_FRAME_COUNT} frames
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default GifFrameSelector;
